import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { accountQuery } from 'queries/accounts/queries';
import { useAccount } from 'queries/accounts/useAccount';
import { useLoyaltyTiers } from 'queries/accounts/useLoyaltyTiers';
import { useLoyaltySession } from 'queries/tickets/useLoyaltySession';
import { RefetchTime } from 'queries/types';
import {
  useLoyaltyStore,
  useLoyaltyStoreActions,
} from 'services/stores/loyalty';

import LoyaltyCelebration from './LoyaltyCelebration';

const LoyaltyCelebrationContainer = () => {
  const { account } = useAccount();
  const { loyaltyTiers } = useLoyaltyTiers();
  const [refetchInterval, setRefetchInterval] = useState(0);
  const queryClient = useQueryClient();

  const [shouldPlay, setShouldPlay] = useState(false);

  const { setHasPlayed } = useLoyaltyStoreActions();
  const [cartId, previous, hasPlayed] = useLoyaltyStore(
    useShallow((state) => [state.cartId, state.previous, state.hasPlayed]),
  );
  const { loyaltySession } = useLoyaltySession(cartId, {
    refetchInterval,
    enabled: !!cartId,
  });

  const addLoyaltyPointsEffect = loyaltySession?.effects?.find(
    (effect) => effect.type === 'addLoyaltyPoints',
  );

  const hasData =
    (!!cartId || !!addLoyaltyPointsEffect) &&
    !!account &&
    !!addLoyaltyPointsEffect &&
    !!loyaltyTiers;

  const canShowCelebration = !hasPlayed && hasData;

  useEffect(() => {
    if (canShowCelebration && !shouldPlay) {
      // Decide to play this animation
      setShouldPlay(true);
      // Mark as played in persistent store immediately
      setHasPlayed();
    }
  }, [canShowCelebration, shouldPlay, setHasPlayed]);

  useEffect(() => {
    if (loyaltySession?.state === 'open') {
      setRefetchInterval(RefetchTime.FIVE_SECONDS);
    } else {
      setRefetchInterval(0);
      // When the T1 session is closed, only then the points/tier will be updated,
      // let's ensure we invalidate the account query so the user sees the updated points/tier in their account.
      queryClient.invalidateQueries({ queryKey: accountQuery.queryKey });
    }
  }, [loyaltySession?.state, queryClient]);

  if (!hasData || !shouldPlay) {
    return null;
  }

  const previousPoints = previous?.points;
  const newPoints = addLoyaltyPointsEffect?.value + previousPoints;

  const tiersToAnimate = loyaltyTiers?.filter(
    (tier) =>
      tier.id === previous.tier ||
      (tier.minPoints <= newPoints && tier.minPoints > previousPoints),
  );

  if (!tiersToAnimate?.length || !addLoyaltyPointsEffect?.value) {
    return null;
  }

  return (
    <LoyaltyCelebration
      isSaudiRewardsLinked={!!account?.saudiRewardsMemberId}
      totalPointsGained={addLoyaltyPointsEffect.value}
      tiersToAnimate={tiersToAnimate}
      loyaltyTiers={loyaltyTiers}
      previousState={previous}
    />
  );
};

export default LoyaltyCelebrationContainer;
